import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import pick from 'lodash/pick';
import a from '../../../../../redux/actions';
import s from '../../../../../redux/selectors';
import { wrap, withT } from '../../../../../lib/wrappers';
import makeInputValue from '../../../../../lib/makeInputValue';
import InlineInput from '../InlineInput';
import InlineSelect from '../InlineSelect';
import types from '../../../../../types';

const transportationOptions = t => [
  { value: '0', display: t('a diesel bus') },
  { value: '1', display: t('a natural gas bus') },
  { value: '2', display: t('transit rail') },
  { value: '3', display: t('Amtrak') },
];

const vehicleTradeInOptions = (vehicles, t) => vehicles
  .filter(vehicle => !vehicle.isElectric)
  .map(vehicle => ({ value: vehicle.index, display: t('Vehicle {{index}}', vehicle) }));

const TakePublicTransportation = ({
  t,
  locale,
  isMetric,
  widgetSource,
  vehicles,
  updateTradeInVehicle,
  publicTransportationTradeInVehicle,
}) => (
  <div className="action-details">
    <div className="action-details__form">
      <p>
        {t('I will take')}
        <InlineSelect
          apiKey="input_takeaction_take_public_transportation_type"
          options={transportationOptions(t)}
        />
        {locale === 'fr' && t('pour parcourir')}
        <InlineInput
          apiKey="input_takeaction_take_public_transportation_miles"
          maxLength={3}
        />
        {t('{{distanceUnit}} per week instead of driving', {
          distanceUnit: isMetric ? t('km', { ns: 'units' }) : t('mi', { ns: 'units' }),
        })}
        <InlineSelect
          ariaLabel={t('vehicle')}
          options={vehicleTradeInOptions(vehicles, t)}
          inputValue={makeInputValue(publicTransportationTradeInVehicle.index, 1)}
          onChange={value => updateTradeInVehicle('take_public_transportation', value)}
        />
        {t('which has a fuel economy of {{efficiencyDisplay}} {{fuelEfficiencyUnit}}. I will save', publicTransportationTradeInVehicle)}
        <InlineInput
          currency
          apiKey={'input_takeaction_take_public_transportation_parking'}
        />
        {t('on parking and spend')}
        <InlineInput
          currency
          apiKey={'input_takeaction_take_public_transportation_ptfare'}
        />
        {t('per week on public transportation.')}
      </p>
    </div>
    <div className="action-details__facts">
      {t('Riding transit frees you from the stress of navigating traffic and provides time for work, catching up on the news or finally getting around to that book you\'ve been meaning to read.')}
    </div>
    {widgetSource !== 'ontario' &&
      <div className="action-details__links">
        <a
          href="http://www.coolcalifornia.org/funding-wizard#search//%255BZipcode%253D%253D94720%255D%255BCategory%253D%253DTransportation%255D%255BEligible+Applicants%253D%253DIndividual%255D/false/none/false/1/12"
          target="_blank"
          rel="noopener noreferrer"
        >{t('Find rebates and incentives for this action')}</a>
      </div>
    }
    <div className="action-details__assumptions">
      <div className="action-details__assumptions__header">{t('Assumptions')}</div>
      <ul>
        <li>
          {t('Price of gasoline:')}
          <InlineInput
            currency
            apiKey="input_takeaction_assumption_price_of_gasoline"
          />
          {t('per {{volumeUnit}}', { volumeUnit: isMetric ? t('L', { ns: 'units' }) : t('gal', { ns: 'units' }) })}
        </li>
        <li>
          <span
            dangerouslySetInnerHTML={{
              __html: t('Carbon intensity of different transit options (gCO<sub>2</sub>eq/passenger {{distanceUnit}})', {
                distanceUnit: isMetric ? t('km', { ns: 'units' }) : t('mi', { ns: 'units' }),
              }),
            }}
          />
          <ul>
            <li>
              {t('Diesel bus')}
              <InlineInput
                maxLength={4}
                apiKey={'input_takeaction_take_public_transportation_gco2bus'}
              />
              {t(', average occupancy of bus is 11 passengers.')}
            </li>
            <li>
              {t('Natural gas bus')}
              <InlineInput
                maxLength={4}
                apiKey={'input_takeaction_take_public_transportation_gco2ngbus'}
              />
              {t(', average occupancy is 11 passengers.')}
            </li>
            <li>
              {t('Electric transit rail')}
              <InlineInput
                maxLength={4}
                apiKey={'input_takeaction_take_public_transportation_gco2transit'}
              />
              {t(', average occupancy is 37 passengers per rail car.')}
            </li>
            <li>
              {t('Amtrak')}
              <InlineInput
                maxLength={4}
                apiKey={'input_takeaction_take_public_transportation_gco2amtrak'}
              />
              {t(', average occupancy is 23 passengers per rail car.')}
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </div>
);

TakePublicTransportation.propTypes = {
  // wrapped
  t: PropTypes.func.isRequired,
  // connected props
  isMetric: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  widgetSource: PropTypes.string.isRequired,
  vehicles: types.Vehicles.isRequired,
  updateTradeInVehicle: PropTypes.func.isRequired,
  publicTransportationTradeInVehicle: types.Vehicle.isRequired,
};

const mapStateToProps = (state, ownProps) => [
  'isMetric',
  'locale',
  'widgetSource',
  'vehicles',
  'publicTransportationTradeInVehicle',
].reduce((props, selector) => ({
  ...props,
  [selector]: s[selector](state, ownProps),
}), {});

const mapDispatchToProps = dispatch => bindActionCreators(
  pick(a, [
    'updateTradeInVehicle',
  ]),
  dispatch,
);

export default wrap(
  withT('takePublicTransportation'),
  connect(mapStateToProps, mapDispatchToProps)(TakePublicTransportation),
);
